/* eslint-disable max-lines */
import { Modal, Table, Button, Row, Col, FormGroup, Form, Input } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import AttendancesService from '../../../services/Attendances';
import PayCodesService from '../../../services/PayCodes';
import PayCodesModal from './PayCodesModal';
import isAdmin from '../../../helpers/isAdmin';
import isDirector from '../../../helpers/isDirector';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import isUpperManagement from '../../../helpers/isUpperManagement';
import TimezonePicker from '../../../components/Core/TimezonePicker';

type TimeCardEditorModalProps = {
  userId: string;
  dateSelected: string;
  isOpen: boolean;
  toggleModal: (e?: any) => void;
};

const TimeCardEditorModal = ({ userId, dateSelected, isOpen, toggleModal }: TimeCardEditorModalProps) => {
  const { isMobile } = useCheckMobileScreen();
  const { token, user, currentSchool }: any = useContext(AuthContext);
  const hasPermission = isAdmin(user) || isDirector(user) || isUpperManagement(user);
  const [loading, setLoading] = useState(false);
  const [payCodes, setPayCodes] = useState([] as any[]);
  const [attendancesList, setAttendancesList] = useState([] as any[]);
  const [dataList, setDataList] = useState([] as any[]);
  const [isTypesOpen, setIsTypesOpen] = useState(false);
  const [payCode, setPayCode] = useState('');
  const [description, setDescription] = useState('');
  const [clockIn, setClockIn] = useState(undefined as undefined | string);
  const [clockOut, setClockOut] = useState(undefined as undefined | string);
  const [startBreak, setStartBreak] = useState(undefined as undefined | string);
  const [endBreak, setEndBreak] = useState(undefined as undefined | string);
  const [isEdit, setIsEdit] = useState(false);
  const [currentClockInId, setCurrentClockInId] = useState('');
  const [currentClockOutId, setCurrentClockOutId] = useState('');
  const [currentStartBreakId, setCurrentStartBreakId] = useState('');
  const [currentEndBreakId, setCurrentEndBreakId] = useState('');
  const disableFormFields = !!attendancesList.find((a) => a.isHoliday || a.isTimeOff);

  useEffect(() => {
    if (isOpen) {
      listTypes();
      listData();
    }
  }, [isOpen]);

  const listTypes = async () => {
    try {
      const result = await PayCodesService(token).list();
      setPayCodes(result.data || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No Types Found!');
    }
  };

  const listData = async () => {
    try {
      const query = `startDateTime=${dateSelected}T00:00:00.000Z&finalDateTime=${dateSelected}T23:59:59.000Z`;
      const result = await AttendancesService(token).listByUser(userId, query);
      const data = result?.data || [];
      setDataList(data);
      const clockIns = data.filter((d: any) => d.attendanceType === 'CLOCKIN');
      const clockOuts = data.filter((d: any) => d.attendanceType === 'CLOCKOUT');
      const startBreaks = data.filter((d: any) => d.attendanceType === 'STARTBREAK');
      const endBreaks = data.filter((d: any) => d.attendanceType === 'ENDBREAK');
      const newList = [];
      for (let i = 0; i <= clockIns?.length; i++) {
        if (clockIns?.[i]) {
          newList.push({
            clockInId: clockIns?.[i]?.id || '',
            startBreakId: startBreaks?.[i]?.id || '',
            endBreakId: endBreaks?.[i]?.id || '',
            clockOutId: clockOuts?.[i]?.id || '',
            clockIn: clockIns?.[i]?.createdAt ? moment(clockIns[i].createdAt).format('hh:mm A') : '',
            startBreak: startBreaks?.[i]?.createdAt ? moment(startBreaks[i].createdAt).format('hh:mm A') : '-',
            endBreak: endBreaks?.[i]?.createdAt ? moment(endBreaks[i].createdAt).format('hh:mm A') : '-',
            clockOut: clockOuts?.[i]?.createdAt ? moment(clockOuts[i].createdAt).format('hh:mm A') : '',
            payCode: clockIns?.[i]?.payCode || '',
            description: clockIns?.[i]?.description || '',
            isHoliday: clockIns?.[i]?.isHoliday || false,
            isTimeOff: clockIns?.[i]?.isTimeOff || false,
          });
        }
      }
      setAttendancesList(newList);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No Data Found!');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const clockInMoment = moment(`${dateSelected} ${moment(clockIn).format('HH:mm:ss Z')}`);
    const startBreakMoment = moment(`${dateSelected} ${moment(startBreak).format('HH:mm:ss')}`);
    const endBreakMoment = moment(`${dateSelected} ${moment(endBreak).format('HH:mm:ss')}`);
    const clockOutMoment = moment(`${dateSelected} ${moment(clockOut).format('HH:mm:ss')}`);
    if (clockInMoment.isValid() && clockOutMoment.isValid() && clockInMoment > clockOutMoment) {
      toast.warn(`The clock in time cannot be greater than the clock out time.`);
      return;
    }
    if (startBreakMoment.isValid() && clockOutMoment.isValid() && startBreakMoment > clockOutMoment) {
      toast.warn(`The start break time cannot be greater than the clock out time.`);
      return;
    }
    if (endBreakMoment.isValid() && clockOutMoment.isValid() && endBreakMoment > clockOutMoment) {
      toast.warn(`The end break time cannot be greater than the clock out time.`);
      return;
    }
    setLoading(true);
    try {
      const body = {
        roomId: currentSchool.value || '',
        roomType: 'SCHOOL',
        payCode,
        description,
        clockIn: clockInMoment.isValid() ? `${dateSelected} ${clockInMoment.format('HH:mm:ss Z')}` : '',
        startBreak: startBreakMoment.isValid() ? `${dateSelected} ${startBreakMoment.format('HH:mm:ss Z')}` : '',
        endBreak: endBreakMoment.isValid() ? `${dateSelected} ${endBreakMoment.format('HH:mm:ss Z')}` : '',
        clockOut: clockOutMoment.isValid() ? `${dateSelected} ${clockOutMoment.format('HH:mm:ss Z')}` : '',
      };

      isEdit
        ? await AttendancesService(token).updateByUser(userId, {
            ...body,
            clockInId: currentClockInId,
            startBreakId: currentStartBreakId,
            endBreakId: currentEndBreakId,
            clockOutId: currentClockOutId,
          })
        : await AttendancesService(token).createByUser(userId, body);

      toast.success(`Saved successfully!`);
      listData();
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error on save!');
      toast.error(message);
    } finally {
      setLoading(false);
      handleCancelEdit();
    }
  };

  const handleDelete = async (
    e: React.FormEvent,
    clockInId: string,
    startBreakId: string,
    endBreakId: string,
    clockOutId: string,
  ) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      if (clockInId) {
        await AttendancesService(token).delete(clockInId);
      }
      if (startBreakId) {
        await AttendancesService(token).delete(startBreakId);
      }
      if (endBreakId) {
        await AttendancesService(token).delete(endBreakId);
      }
      if (clockOutId) {
        await AttendancesService(token).delete(clockOutId);
      }
      toast.success(`Deleted successfully!`);
      listData();
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Delete error!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (
    e: React.FormEvent,
    clockInId: string,
    startBreakId: string,
    endBreakId: string,
    clockOutId: string,
    payCodeId: string,
    descriptionId: string,
  ) => {
    e.preventDefault();
    setCurrentClockInId(clockInId || '');
    setCurrentStartBreakId(startBreakId || '');
    setCurrentEndBreakId(endBreakId || '');
    setCurrentClockOutId(clockOutId || '');
    const isDataEdit = !!(clockInId || startBreakId || endBreakId || clockOutId);
    setIsEdit(isDataEdit);
    if (isDataEdit) {
      let timeClockIn = dataList.find((d: any) => d.id === clockInId)?.createdAt || undefined;
      if (timeClockIn) {
        timeClockIn = moment(timeClockIn).format('YYYY-MM-DD HH:mm:ss');
      }
      setClockIn(timeClockIn);
      let timeStartBreak = dataList.find((d: any) => d.id === startBreakId)?.createdAt || undefined;
      if (timeStartBreak) {
        timeStartBreak = moment(timeStartBreak).format('YYYY-MM-DD HH:mm:ss');
      }
      setStartBreak(timeStartBreak);
      let timeEndBreak = dataList.find((d: any) => d.id === endBreakId)?.createdAt || undefined;
      if (timeEndBreak) {
        timeEndBreak = moment(timeEndBreak).format('YYYY-MM-DD HH:mm:ss');
      }
      setEndBreak(timeEndBreak);
      let timeClockOut = dataList.find((d: any) => d.id === clockOutId)?.createdAt || undefined;
      if (timeClockOut) {
        timeClockOut = moment(timeClockOut).format('YYYY-MM-DD HH:mm:ss');
      }
      setClockOut(timeClockOut);
      setPayCode(payCodeId || '');
      setDescription(descriptionId || '');
    }
  };

  const handleCancelEdit = () => {
    setCurrentClockInId('');
    setCurrentStartBreakId('');
    setCurrentEndBreakId('');
    setCurrentClockOutId('');
    setIsEdit(false);
    setClockIn(undefined);
    setClockOut(undefined);
    setPayCode('');
    setDescription('');
  };

  const toggleModalTypes = (e: any) => {
    e?.preventDefault();
    setIsTypesOpen(!isTypesOpen);
    listTypes();
  };

  return (
    <>
      <Modal
        className="modal-dialog-centered"
        size="lg"
        isOpen={isOpen}
        style={{ maxWidth: '90%' }}
        toggle={(e: any) => {
          handleCancelEdit();
          toggleModal(e);
        }}
      >
        <div className="modal-header pb-1">
          <h5 className="modal-title">Time Card Editor - {moment(dateSelected).format('MM/DD/yyyy')}</h5>

          <div className="text-right mb-3">
            {hasPermission && (
              <i className="fas fa-cog fa-lg" style={{ cursor: 'pointer' }} onClick={toggleModalTypes}></i>
            )}

            <button
              aria-label="Close"
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={(e) => {
                handleCancelEdit();
                toggleModal(e);
              }}
            >
              <span aria-hidden={true}>×</span>
            </button>
          </div>
        </div>
        <div className="modal-body pt-0">
          <Row>
            <Col>
              <Form onSubmit={handleSubmit}>
                <fieldset disabled={disableFormFields}>
                  <Row>
                    <div className="col-lg col-md">
                      <FormGroup>
                        <label className="form-control-label">Clock In</label>
                        <TimezonePicker
                          defaultValue={clockIn}
                          value={clockIn}
                          onChangeTime={(clockIn: string) => {
                            setClockIn(clockIn);
                          }}
                          disabled={disableFormFields}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-lg col-md">
                      <FormGroup>
                        <label className="form-control-label">Start Break</label>
                        <TimezonePicker
                          defaultValue={startBreak}
                          value={startBreak}
                          onChangeTime={(time: string) => {
                            setStartBreak(time);
                          }}
                          disabled={disableFormFields}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-lg col-md">
                      <FormGroup>
                        <label className="form-control-label">End Break</label>
                        <TimezonePicker
                          defaultValue={endBreak}
                          value={endBreak}
                          onChangeTime={(time: string) => {
                            setEndBreak(time);
                          }}
                          disabled={disableFormFields}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-lg col-md">
                      <FormGroup>
                        <label className="form-control-label">Clock Out</label>
                        <TimezonePicker
                          defaultValue={clockOut}
                          value={clockOut}
                          onChangeTime={(time: string) => {
                            setClockOut(time);
                          }}
                          disabled={disableFormFields}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-lg col-md-12">
                      <FormGroup>
                        <label className="form-control-label">Pay Code</label>
                        <Input type="select" value={payCode} onChange={(e) => setPayCode(e.target.value)}>
                          <option value=""></option>
                          {payCodes.map((a: any, index: number) => {
                            return (
                              <option value={a.name} key={`codes-${index}`}>
                                {a.name}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    </div>
                  </Row>
                  <Row>
                    <Col md="12" className="mb-3 text-right">
                      <Button color="primary" type="submit" size="sm">
                        {isEdit ? 'Edit' : 'Add'}
                      </Button>
                      {isEdit && (
                        <Button color="danger" type="button" size="sm" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      )}
                    </Col>
                  </Row>
                </fieldset>
              </Form>
            </Col>
          </Row>
          {isMobile && (
            <Table className="align-items-center table-flush table-timecard-mobile" responsive>
              <tbody>
                {attendancesList.map((s: any, index: number) => (
                  <tr key={`attendances-modal-${index}`}>
                    <td className="pr-2">
                      <p className="m-0">
                        <b>Clock IN</b>{' '}
                        <div className="float-right text-center" style={{ width: '60px' }}>
                          {moment(`${dateSelected} ${s.clockIn}`).tz(currentSchool.timezone).format('hh:mm A')}
                        </div>
                      </p>
                      <p className="m-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                        <b>Start Break</b>
                        <div className="float-right text-center" style={{ width: '60px' }}>
                          {moment(`${dateSelected} ${s.startBreak}`).tz(currentSchool.timezone).format('hh:mm A')}
                        </div>
                      </p>
                      <p className="m-0">
                        <b>End Break</b>
                        <div className="float-right text-center" style={{ width: '60px' }}>
                          {moment(`${dateSelected} ${s.endBreak}`).tz(currentSchool.timezone).format('hh:mm A')}
                        </div>
                      </p>
                      <p className="m-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                        <b>Clock OUT</b>
                        <div className="float-right text-center" style={{ width: '60px' }}>
                          {moment(`${dateSelected} ${s.clockOut}`).tz(currentSchool.timezone).format('hh:mm A')}
                        </div>
                      </p>
                      <p className="m-0">
                        <b>Pay Code</b>
                        <div className="float-right text-center" style={{ width: '60px' }}>
                          {s.payCode}
                        </div>
                      </p>
                    </td>
                    <td
                      align="center"
                      style={{ verticalAlign: 'top', paddingTop: '30px', width: '90px' }}
                      className="pl-2"
                    >
                      {!isEdit && !s.isTimeOff && !s.isHoliday && (
                        <>
                          <button
                            className="btn btn-primary btn-md m-0 w-100 d-block mb-2"
                            onClick={(e) =>
                              handleEdit(
                                e,
                                s.clockInId,
                                s.startBreakId,
                                s.endBreakId,
                                s.clockOutId,
                                s.payCode,
                                s.description,
                              )
                            }
                          >
                            <i className="far fa-edit" style={{ cursor: 'pointer' }} title="Edit" />
                          </button>
                          <button
                            className="btn btn-danger btn-md m-0 w-100 d-block"
                            onClick={(e) => handleDelete(e, s.clockInId, s.startBreakId, s.endBreakId, s.clockOutId)}
                          >
                            <i className="far fa-trash-alt" style={{ cursor: 'pointer' }} title="Delete" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {!isMobile && (
            <Table className="align-items-center table-flush" responsive>
              <tbody>
                <tr>
                  <th>Clock IN</th>
                  <th>Start Break</th>
                  <th>End Break</th>
                  <th>Clock OUT</th>
                  <th>Pay Code</th>
                  {/* <th>Description</th> */}
                  <th>Action</th>
                </tr>
                {attendancesList?.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      Data not found
                    </td>
                  </tr>
                )}
                {attendancesList.map((s: any, index: number) => (
                  <tr key={`attendances-modal-${index}`}>
                    <td>{moment(`${dateSelected} ${s.clockIn}`).tz(currentSchool.timezone).format('hh:mm A')}</td>
                    <td>
                      {s.startBreak === '-'
                        ? '-'
                        : moment(`${dateSelected} ${s.startBreak}`).tz(currentSchool.timezone).format('hh:mm A')}
                    </td>
                    <td>
                      {s.endBreak === '-'
                        ? '-'
                        : moment(`${dateSelected} ${s.endBreak}`).tz(currentSchool.timezone).format('hh:mm A')}
                    </td>
                    <td>{moment(`${dateSelected} ${s.clockOut}`).tz(currentSchool.timezone).format('hh:mm A')}</td>
                    <td>{s.payCode}</td>
                    {/* <td>{s.description}</td> */}
                    <td>
                      {!isEdit && (
                        <>
                          {(s.isTimeOff || s.isHoliday) && (
                            <>{s.isTimeOff ? 'TimeOff' : 'Holiday'} canot edit here, click here to edit</>
                          )}
                          {!s.isTimeOff && !s.isHoliday && (
                            <>
                              <i
                                className="far fa-edit text-primary mr-2"
                                style={{ cursor: 'pointer' }}
                                title="Edit"
                                onClick={(e) =>
                                  handleEdit(
                                    e,
                                    s.clockInId,
                                    s.startBreakId,
                                    s.endBreakId,
                                    s.clockOutId,
                                    s.payCode,
                                    s.description,
                                  )
                                }
                              ></i>
                              <i
                                className="far fa-trash-alt text-danger"
                                style={{ cursor: 'pointer' }}
                                title="Delete"
                                onClick={(e) =>
                                  handleDelete(e, s.clockInId, s.startBreakId, s.endBreakId, s.clockOutId)
                                }
                              ></i>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Modal>
      <PayCodesModal isOpen={isTypesOpen} toggleModal={(e) => toggleModalTypes(e)} />
    </>
  );
};

export default TimeCardEditorModal;
